import { useMemo } from 'react';
import { INERT_ORDER_INTERACTION, toOrderCardActions } from '@hopdrive/feature-order-index';
import type { OrderInteraction } from '@hopdrive/feature-order-index';

import { useScenarioActions } from '@components/ScenarioActions';

/**
 * The dealer's answer to the package's `OrderInteraction` contract: the portal's existing
 * interaction layer (the photo viewer, the assign / change-time / cancel dialogs, guest
 * chat, navigation) keyed as an order's action descriptors are.
 *
 * `ScenarioActionsProvider` builds an order's handlers from its view; this maps them onto
 * the three fields the package asks for. A Valet order's id is its scenario id, which is
 * what the provider tracks as assigning. Outside the provider the layer reads inert, so a
 * card rendered in a preview comes out readable and actionless rather than throwing.
 */
export const useDealerOrderInteraction = (): OrderInteraction => {
  const actions = useScenarioActions();
  return useMemo(() => {
    if (!actions) return INERT_ORDER_INTERACTION;
    type ViewInput = Parameters<typeof actions.buildCardActions>[0];
    return {
      resolveCardActions: (model, view) => (model.product === 'moves' ? toOrderCardActions(actions.buildCardActions(view as ViewInput)) : undefined),
      isAssigning: model => model.product === 'moves' && actions.assigningScenarioIds.has(model.id),
      onOpenPhotos: (itemId, phase) => actions.openLegPhotos(itemId, phase),
    };
  }, [actions]);
};
